import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Table: tabular data (transaction history, upcoming payments, saved methods) on desktop.
 *
 * This is a desktop pattern: below `lg`, reflow rows into stacked card list items rather than
 * letting the table scroll sideways. Wrap it in a `Card` for the standard bordered surface.
 *
 * For a scrolling history, pass `maxHeight` here and `sticky` on `TableHeader`.
 */
function Table({
  className,
  maxHeight,
  containerClassName,
  ...props
}: React.ComponentProps<'table'> & {
  /** Caps the scroll container's height. A bare number is treated as pixels. */
  maxHeight?: string | number;
  containerClassName?: string;
}) {
  const cappedHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  return (
    <div
      data-slot="table-container"
      className={cn(
        'relative w-full overflow-x-auto',
        cappedHeight && 'max-h-[var(--table-max-height)] overflow-y-auto',
        containerClassName,
      )}
      // A caller-supplied height can't be a static utility, so it rides in on a CSS variable that
      // the `max-h-[…]` class above reads.
      style={cappedHeight ? ({ '--table-max-height': cappedHeight } as React.CSSProperties) : undefined}
    >
      <table data-slot="table" className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  );
}

function TableHeader({
  className,
  sticky,
  ...props
}: React.ComponentProps<'thead'> & {
  /** Pins the header while the body scrolls. Pair with `maxHeight` on `Table`. */
  sticky?: boolean;
}) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b', sticky && 'sticky top-0 z-10 bg-card', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn('border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted', className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'whitespace-nowrap p-3 text-left align-middle text-xs font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn('p-3 align-middle text-foreground [&:has([role=checkbox])]:pr-0', className)}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption data-slot="table-caption" className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
  );
}

/* ── states ────────────────────────────────────────────────────
 * Loading / empty / error as composable parts rather than props on Table, so Table keeps its
 * markup-shaped API. Each renders `<tr>`s: put them inside `<TableBody>`.
 */

/** The DS's skeleton widths, cycled across rows and columns so the placeholder doesn't read as a grid. */
const SKELETON_WIDTHS = ['w-[70%]', 'w-[45%]', 'w-[60%]', 'w-[38%]', 'w-[55%]'];

/**
 * Skeleton rows while the table's data loads. The rows are `aria-busy` and the bars hidden from
 * assistive tech; a single visually hidden status carries `label` instead.
 */
function TableLoading({
  rows = 3,
  columns,
  label,
  className,
  ...props
}: Omit<React.ComponentProps<'tr'>, 'children'> & {
  rows?: number;
  columns: number;
  /** Visually hidden loading status. Defaults to a generic "Loading…". */
  label?: string;
}) {
  const { t } = useTranslation('shared');

  return Array.from({ length: rows }, (_, r) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows have no identity and never reorder.
    <TableRow key={r} aria-busy="true" className={cn('hover:bg-transparent', className)} {...props}>
      {Array.from({ length: columns }, (_, c) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: same — fixed placeholder cells.
        <TableCell key={c}>
          {r === 0 && c === 0 && (
            <span role="status" className="sr-only">
              {label ?? t('table.loading')}
            </span>
          )}
          <Skeleton aria-hidden="true" className={cn('h-4', SKELETON_WIDTHS[(r + c) % SKELETON_WIDTHS.length])} />
        </TableCell>
      ))}
    </TableRow>
  ));
}

/** A single full-width row for a table with no rows to show. Callers pass translated copy. */
function TableEmpty({
  colSpan,
  icon,
  title,
  description,
  action,
  className,
  ...props
}: Omit<React.ComponentProps<'td'>, 'title' | 'children'> & {
  colSpan: number;
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className={cn('py-10 text-center', className)} {...props}>
        <div className="mx-auto flex max-w-sm flex-col items-center gap-2">
          {icon}
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          {action}
        </div>
      </TableCell>
    </TableRow>
  );
}

/** A single full-width alert row for a table whose data failed to load. Callers pass translated copy. */
function TableError({
  colSpan,
  title,
  description,
  action,
  className,
  ...props
}: Omit<React.ComponentProps<'td'>, 'title' | 'children'> & {
  colSpan: number;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className={cn('py-10 text-center', className)} {...props}>
        <div role="alert" className="mx-auto flex max-w-sm flex-col items-center gap-2">
          <p className="text-sm font-semibold text-destructive-foreground">{title}</p>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          {action}
        </div>
      </TableCell>
    </TableRow>
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableEmpty,
  TableError,
  TableFooter,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
};
